import { AxiosError } from 'axios';

export interface ParsedApiError {
  message: string;
  fieldErrors: Record<string, string>;
}

/** Extract validation field errors and message from API errors for inline form display */
export const parseApiError = (error: unknown): ParsedApiError => {
  const fieldErrors: Record<string, string> = {};
  if (error instanceof AxiosError) {
    const data = error.response?.data as
      | { validation?: Record<string, string | string[]> }
      | undefined;
    if (data?.validation && typeof data.validation === 'object') {
      for (const [k, v] of Object.entries(data.validation)) {
        fieldErrors[k] = Array.isArray(v) ? v.join(', ') : String(v);
      }
    }
  }
  return { message: errorHandler(error), fieldErrors };
};

interface ErrorResponse {
  message?: string;
  error?: string;
  errors?: Record<string, string | string[]> | string[];
  validation?: Record<string, string | string[]> | string;
}

export const errorHandler = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const status = error.response?.status;
    const responseData = error.response?.data as ErrorResponse | undefined;
    const requestUrl = error.config?.url || 'resource';

    // ALWAYS check for backend error messages first (highest priority)
    if (responseData) {
      // Check for validation errors FIRST (most specific)
      if (responseData.errors) {
        // Check if it's an array first
        if (Array.isArray(responseData.errors)) {
          if (responseData.errors.length > 1) {
            return (
              'Please fix the following errors:\n• ' +
              responseData.errors.join('\n• ')
            );
          }
          return responseData.errors.join('; ');
        }
        // Then check if it's an object (Record)
        if (typeof responseData.errors === 'object') {
          const errorMessages = Object.entries(responseData.errors)
            .map(([field, message]) => {
              if (Array.isArray(message)) {
                return `${field}: ${message.join(', ')}`;
              }
              return `${field}: ${message}`;
            })
            .join('\n• ');
          if (errorMessages) {
            return Object.keys(responseData.errors).length > 1
              ? 'Please fix the following errors:\n• ' + errorMessages
              : errorMessages;
          }
        }
      }

      // Check for validation field
      if (responseData.validation) {
        if (typeof responseData.validation === 'object') {
          const validationMessages = Object.entries(responseData.validation)
            .map(([field, message]) => {
              if (Array.isArray(message)) {
                return `${field}: ${message.join(', ')}`;
              }
              return `${field}: ${message}`;
            })
            .join('\n• ');
          if (validationMessages) {
            return Object.keys(responseData.validation).length > 1
              ? 'Please fix the following errors:\n• ' + validationMessages
              : validationMessages;
          }
        }
        return String(responseData.validation);
      }

      // Check for error field
      if (responseData.error) {
        return responseData.error;
      }

      // Check for direct message from backend (lowest priority for validation errors)
      if (responseData.message) {
        return responseData.message;
      }
    }

    // Only if no backend message, fall back to generic messages based on status
    if (status) {
      switch (status) {
        case 404: {
          // Extract resource type from URL if possible
          const resourceType = requestUrl.includes('/recipes/')
            ? 'Remedy'
            : requestUrl.includes('/doctors/')
              ? 'Doctor'
              : requestUrl.includes('/admin/')
                ? 'Resource'
                : 'Resource';
          return `${resourceType} not found. The ${resourceType.toLowerCase()} you're looking for doesn't exist or you don't have permission to access it.`;
        }
        case 403:
          return "Access forbidden. You don't have permission to access this resource.";
        case 401:
          return 'Unauthorized. Please log in to access this resource.';
        case 400:
          return 'Bad request. Please check your input and try again.';
        case 500:
          return 'Server error. The server encountered an error. Please try again later.';
        case 502:
          return 'Bad gateway. The server is temporarily unavailable. Please try again later.';
        case 503:
          return 'Service unavailable. The service is temporarily down. Please try again later.';
        default:
          return `Request failed with status ${status}. Please try again.`;
      }
    }

    // If no status code, check for error message
    if (error.message) {
      return error.message;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Something went wrong. Please try again.';
};
